/**
 * Bilingual date formatter — Hijri in AR mode, Gregorian in EN mode.
 * Uses Intl.DateTimeFormat (built into all modern browsers, no extra library).
 *
 * Usage:
 *   import { formatDate, formatRelative, formatDateTime } from '../utils/dateFormat';
 *   formatDate('2026-06-15', 'ar')     // "29 ذو القعدة 1447 هـ"
 *   formatDate('2026-06-15', 'en')     // "Jun 15, 2026"
 *   formatRelative('2026-06-14', 'ar') // "منذ يوم واحد"
 *   formatDateTime(date, 'ar')         // includes time component
 */

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Format a date for display. Auto-picks calendar based on language.
 * @param {Date|string|number} value - any date-like value
 * @param {"ar"|"en"} lang - language code (default: "ar")
 * @param {boolean} showHijriSuffix - append "هـ" for Hijri (default true)
 */
export function formatDate(value, lang = 'ar', showHijriSuffix = true) {
  const d = toDate(value);
  if (!d) return '';

  if (lang === 'ar') {
    // Islamic / Umm al-Qura calendar — used in Saudi Arabia
    const fmt = new Intl.DateTimeFormat('ar-SA-u-ca-islamic-umalqura', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
    const formatted = fmt.format(d);
    // Intl already includes "هـ" in some locales — only add if missing
    return showHijriSuffix && !formatted.includes('هـ') ? `${formatted} هـ` : formatted;
  }

  return new Intl.DateTimeFormat('en-US', {
    day: 'numeric', month: 'short', year: 'numeric',
  }).format(d);
}

/**
 * Date + time (HH:mm)
 */
export function formatDateTime(value, lang = 'ar') {
  const d = toDate(value);
  if (!d) return '';
  const date = formatDate(d, lang);
  const time = new Intl.DateTimeFormat(lang === 'ar' ? 'ar-SA' : 'en-US', {
    hour: '2-digit', minute: '2-digit', hour12: lang === 'ar',
  }).format(d);
  return `${date} — ${time}`;
}

/**
 * "Posted 3 days ago" style relative time. Falls back to absolute for >30d.
 */
export function formatRelative(value, lang = 'ar') {
  const d = toDate(value);
  if (!d) return '';
  const diffMs = Date.now() - d.getTime();
  const diffSec = Math.round(diffMs / 1000);
  const future = diffSec < 0;
  const abs = Math.abs(diffSec);

  // For longer than 30 days, show absolute date
  if (abs > 30 * 24 * 3600) return formatDate(d, lang);

  const rtf = new Intl.RelativeTimeFormat(lang === 'ar' ? 'ar' : 'en', { numeric: 'auto' });

  if (abs < 60) return rtf.format(future ? -abs : -abs, 'second').replace('-', '');
  if (abs < 3600) return rtf.format(future ? Math.ceil(abs / 60) : -Math.round(abs / 60), 'minute');
  if (abs < 86400) return rtf.format(future ? Math.ceil(abs / 3600) : -Math.round(abs / 3600), 'hour');
  return rtf.format(future ? Math.ceil(abs / 86400) : -Math.round(abs / 86400), 'day');
}

/**
 * Both calendars at once — "29 ذو القعدة 1447 هـ (15 يونيو 2026)"
 * Useful for forms where the user may not recognize Hijri yet.
 */
export function formatDualDate(value, lang = 'ar') {
  const d = toDate(value);
  if (!d) return '';
  if (lang !== 'ar') return formatDate(d, 'en');
  const hijri = formatDate(d, 'ar', true);
  const gregorian = new Intl.DateTimeFormat('ar-EG', {
    day: 'numeric', month: 'long', year: 'numeric',
  }).format(d);
  return `${hijri} (${gregorian})`;
}

export default { formatDate, formatDateTime, formatRelative, formatDualDate };
